// src/redux/square/squareSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type {
  SquareItem,
  SquareTabKey,
} from "../../types/square/squareTypes";
import { squareApi } from "../../services/square/squareApi";

type SquareTabState = {
  items: SquareItem[];
  next: string | null;
  loading: boolean;
  error?: string;
};

type SquareState = {
  tabs: Record<SquareTabKey, SquareTabState>;
};

const emptyTab = (): SquareTabState => ({
  items: [],
  next: null,
  loading: false,
  error: undefined,
});

const initialState: SquareState = {
  tabs: {
    square: emptyTab(),
    friends: emptyTab(),
    people: emptyTab(),
  },
};

const normalizeNextCursor = (rawNext?: string | null) => {
  if (!rawNext) return null;

  const m = rawNext.match(/[?&]cursor=([^&]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : rawNext;
};

// ------------------------------------------------
// Fetch Square content
// ------------------------------------------------
export const fetchSquare = createAsyncThunk(
  "square/fetch",
  async (
    params: {
      tab: SquareTabKey;
      cursor?: string | null;
      mode?: "recent" | "trending" | "for_you";
    },
    { rejectWithValue }
  ) => {
    try {
      const { tab, cursor, mode } = params;

      if (tab === "people") {
        return {
          tab,
          data: {
            next: null,
            results: [],
          },
        };
      }

      const data = await squareApi.fetch({
        tab,
        cursor,
        mode,
      });

      return { tab, data };
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Square fetch failed");
    }
  }
);

const squareSlice = createSlice({
  name: "square",
  initialState,
  reducers: {
    resetSquareTab(state, action: PayloadAction<SquareTabKey>) {
      const tab = action.payload;
      state.tabs[tab] = emptyTab();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSquare.pending, (state, action) => {
        const tab = action.meta.arg.tab;
        state.tabs[tab].loading = true;
        state.tabs[tab].error = undefined;
      })
      .addCase(fetchSquare.fulfilled, (state, action) => {
        const { tab, data } = action.payload;
        const tabState = state.tabs[tab];

        const isFirstPage = !action.meta.arg.cursor;

        if (isFirstPage) {
          tabState.items = [];
        }

        const seen = new Set<string>(
          tabState.items.map((it) => `${it.kind}-${it.id}`)
        );

        for (const item of data.results as SquareItem[]) {
          const key = `${item.kind}-${item.id}`;
          if (seen.has(key)) continue;

          tabState.items.push(item);
          seen.add(key);
        }

        tabState.next = normalizeNextCursor(data.next);
        tabState.loading = false;
        tabState.error = undefined;
      })
      .addCase(fetchSquare.rejected, (state, action) => {
        const tab = action.meta.arg.tab;
        state.tabs[tab].loading = false;
        state.tabs[tab].error =
          (action.payload as string) || "Square fetch failed.";
      });
  },
});

export const { resetSquareTab } = squareSlice.actions;
export default squareSlice.reducer;